import { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { loadUiSettings } from "../../utils/uiSettings";
import { buttonText } from "../../utils/buttonText";
import { pageText } from "../../utils/pageText";

const RESERVED_GROUPS = new Set(["report", "image", "audio", "table", "Trash"]);
const BUILTIN_GROUPS = ["ALL", "IMAGE", "AUDIO", "TABLE"];

function getCustomGroupNames(datasetItems) {
  const names = new Set();
  datasetItems.forEach((item) => {
    if (item.group && !RESERVED_GROUPS.has(item.group)) {
      names.add(item.group);
    }
  });
  return Array.from(names).sort();
}

function useResolvedGroupNames(groupNames, showGroups) {
  const [fetchedGroupNames, setFetchedGroupNames] = useState([]);

  useEffect(() => {
    if (!showGroups || groupNames.length > 0) return;

    let cancelled = false;
    const loadGroups = async () => {
      const userToken = Cookies.get("user_token");
      try {
        const response = await axios.post("/datasets/list", {
          requesterToken: userToken,
        });
        if (cancelled) return;
        setFetchedGroupNames([
          ...BUILTIN_GROUPS,
          ...getCustomGroupNames(response.data),
        ]);
      } catch {
        if (!cancelled) setFetchedGroupNames(BUILTIN_GROUPS);
      }
    };

    loadGroups();
    return () => {
      cancelled = true;
    };
  }, [groupNames, showGroups]);

  if (!showGroups) return [];

  return groupNames.length > 0
    ? groupNames
    : fetchedGroupNames.length > 0
      ? fetchedGroupNames
      : BUILTIN_GROUPS;
}

function DatasetWorkspaceSidebar({
  storageLabel,
  storageValue,
  groupNames = [],
  selectedGroup,
  onGroupSelect,
  onNavItemSelect,
  showGroups = true,
}) {
  const navigate = useNavigate();
  const groupsVisible = showGroups && loadUiSettings().showDatasetGroups;
  const resolvedGroupNames = useResolvedGroupNames(groupNames, groupsVisible);
  const primaryItems = [
    "Datasets",
    "Trash",
  ];

  const handlePrimaryClick = (item) => {
    if (item === "Datasets" && onGroupSelect) {
      onGroupSelect(null);
    } else if (item === "Trash" && onGroupSelect) {
      onGroupSelect("Trash");
    }
    onNavItemSelect?.(item);
  };

  return (
    <aside className="dataset-sidebar" aria-label={pageText("Dataset workspace navigation")}>
      <div className="dataset-sidebar-section">
        {primaryItems.map((item) => (
          <button
            type="button"
            className={`dataset-sidebar-item clickable ${
              item === "Datasets" && !selectedGroup ? "active" : ""
            } ${item === "Trash" && selectedGroup === "Trash" ? "active" : ""}`}
            key={item}
            onClick={() => handlePrimaryClick(item)}
          >
            <span className="dataset-sidebar-icon" />
            <span>{buttonText(item)}</span>
          </button>
        ))}
      </div>

      {groupsVisible && (
        <div className="dataset-sidebar-groups">
          <p className="dataset-sidebar-title">{pageText("Group")}</p>
          {resolvedGroupNames.map((name) => (
            <button
              type="button"
              className={`dataset-sidebar-item muted clickable ${
                selectedGroup === name ? "active" : ""
              }`}
              key={name}
              onClick={() => onGroupSelect?.(name)}
            >
              <span className="dataset-sidebar-group" />
              <span>{BUILTIN_GROUPS.includes(name) ? pageText(name) : name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="dataset-sidebar-footer">
        {storageLabel && (
          <div className="dataset-sidebar-storage">
            <div className="dataset-sidebar-storage-head">
              <span>{pageText(storageLabel)}</span>
              <strong>{storageValue}</strong>
            </div>
            <div className="dataset-sidebar-storage-track">
              <span className="dataset-sidebar-storage-fill" />
            </div>
          </div>
        )}

        <button
          type="button"
          className="dataset-sidebar-settings"
          aria-label={buttonText("System Settings")}
          title={buttonText("System Settings")}
          onClick={() => navigate("/setting")}
        >
          <FontAwesomeIcon icon={faGear} />
        </button>
      </div>
    </aside>
  );
}

export default DatasetWorkspaceSidebar;
